import {
  daysAgo,
  isSiteRole,
  REQ_GROUPS,
  REQ_STAGE,
  reqIn,
  roDate,
  todayIso,
  type ReqGroup,
  type Requisition,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api } from '../lib/api';
import { currentUser } from '../lib/me';
import type { Route } from '../routes';

const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Încă nu e pe șantier și data a trecut. */
const isLate = (r: Requisition, today: string) =>
  !!r.neededBy && r.neededBy < today && ['open', 'quoting', 'ordering'].includes(r.stage);

/**
 * „Necesar”: ce s-a cerut de pe șantiere. Achizițiile văd tot și pornesc
 * cererea de ofertă din unul sau din mai multe necesare ale aceluiași șantier;
 * omul de pe șantier își vede doar necesarul lui și unde a ajuns.
 */
export function RequisitionsPage({
  go,
  initialGroup,
}: {
  go: (r: Route) => void;
  toast: Toast;
  initialGroup?: ReqGroup;
}) {
  const { data, error, loading, reload } = useAsync(() => api.requisitions(), [], 'requisitions');
  const buyer = !isSiteRole(currentUser().role);
  const [chosen, setGroup] = useState<ReqGroup | null>(initialGroup ?? null);
  const [q, setQ] = useState('');
  const [site, setSite] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const today = todayIso();
  const all = useMemo(() => data?.rows ?? [], [data]);

  // Achizițiile încep cu ce e de preluat, dacă e ceva; șantierul, cu tot.
  const group: ReqGroup = chosen ?? (buyer && data?.groups.open ? 'open' : 'all');

  const sites = useMemo(
    () =>
      [...new Map(all.map((r) => [r.site.id, r.site.name])).entries()].sort((a, b) =>
        a[1].localeCompare(b[1], 'ro'),
      ),
    [all],
  );
  const filtered = useMemo(() => {
    const needle = plain(q.trim());
    return all.filter(
      (r) =>
        (!site || r.site.id === site) &&
        (!needle ||
          plain(
            `${r.number} ${r.title} ${r.createdBy?.name ?? ''} ${r.items.map((i) => i.desc).join(' ')}`,
          ).includes(needle)),
    );
  }, [all, q, site]);
  const rows = filtered.filter((r) => reqIn(r.stage, group));

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} onRetry={reload} />;

  // Bifate pentru o cerere comună: doar de preluat și de pe același șantier.
  const pickedRows = all.filter((r) => picked.includes(r.id));
  const pickSite = pickedRows[0]?.site.id;
  const canPick = (r: Requisition) => buyer && r.status === 'open' && (!pickSite || r.site.id === pickSite);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <>
      <div className="head">
        <div>
          <h1>{buyer ? t('Necesar de pe șantier') : t('Necesarul meu')}</h1>
          <p>
            {buyer
              ? t(
                  'Ce au cerut șefii de șantier. Din necesar faci cererea de ofertă cu un clic: liniile se copiază singure.',
                )
              : t('Ce ai cerut pentru șantier și unde a ajuns: trimis, se cer oferte, comandat, livrat.')}
          </p>
        </div>
        <div className="acts">
          <button className="btn primary" onClick={() => go({ view: 'newRequisition' })}>
            {t('Necesar nou')}
          </button>
        </div>
      </div>

      <div className="count-chips" role="group" aria-label={t('Stadiul necesarului')}>
        {REQ_GROUPS.map((g) => {
          const n = filtered.filter((r) => reqIn(r.stage, g.id)).length;
          return (
            <button
              key={g.id}
              type="button"
              aria-pressed={group === g.id}
              className={g.id === 'open' && buyer && n ? 'alert' : undefined}
              onClick={() => {
                setGroup(g.id);
                setPicked([]);
              }}
            >
              <b>{n}</b>
              <span>{g.label}</span>
            </button>
          );
        })}
      </div>

      {all.length ? (
        <div className="list-tools">
          <input
            className="inp"
            type="search"
            value={q}
            placeholder={t('Caută după număr, material sau cine l-a trimis…')}
            aria-label={t('Caută')}
            onChange={(e) => setQ(e.target.value)}
          />
          {sites.length > 1 ? (
            <select
              className="inp"
              value={site}
              aria-label={t('Șantier')}
              onChange={(e) => setSite(e.target.value)}
            >
              <option value="">{t('Toate șantierele')}</option>
              {sites.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      ) : null}

      {picked.length ? (
        <div className="req-pickbar" role="status">
          <span>
            {picked.length === 1 ? t('Un necesar bifat') : t('{0} necesare bifate', picked.length)} ·{' '}
            {pickedRows[0]?.site.name}
          </span>
          <span className="acts">
            <button className="btn small" onClick={() => setPicked([])}>
              {t('Debifează')}
            </button>
            <button className="btn small primary" onClick={() => go({ view: 'new', requisitionIds: picked })}>
              {t('Fă o cerere de ofertă din ele')}
            </button>
          </span>
        </div>
      ) : null}

      <div className="sheet table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('Necesar')}</th>
              <th>{t('Ce trebuie')}</th>
              <th>{t('Șantier')}</th>
              <th>{t('Trebuie la')}</th>
              {buyer ? <th>{t('Trimis de')}</th> : null}
              <th>{t('Stadiu')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((r) => {
                const st = REQ_STAGE[r.stage];
                const open = () => go({ view: 'requisition', id: r.id });
                const late = isLate(r, today);
                return (
                  <tr
                    key={r.id}
                    onClick={open}
                    className={
                      ['rejected', 'canceled', 'dropped'].includes(r.stage) ? 'muted-row' : undefined
                    }
                  >
                    <td data-label={t('Necesar')}>
                      <span className="req-cell">
                        {canPick(r) ? (
                          <input
                            type="checkbox"
                            className="req-pick"
                            checked={picked.includes(r.id)}
                            aria-label={t('Bifează {0} pentru o cerere de ofertă comună', r.number)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggle(r.id)}
                          />
                        ) : null}
                        <span>
                          <button
                            type="button"
                            className="row-link"
                            onClick={(e) => (e.stopPropagation(), open())}
                          >
                            {r.number}
                          </button>
                          <small>{daysAgo(r.createdAt)}</small>
                        </span>
                      </span>
                    </td>
                    <td data-label={t('Ce trebuie')}>
                      {r.title}
                      <small>
                        {r.items.length === 1 ? t('1 material') : t('{0} materiale', r.items.length)}
                        {r.items[0] ? ` · ${r.items[0].desc}${r.items.length > 1 ? ', …' : ''}` : ''}
                      </small>
                    </td>
                    <td data-label={t('Șantier')}>{r.site.name}</td>
                    <td data-label={t('Trebuie la')} className={late ? 'late-text' : undefined}>
                      {r.neededBy ? roDate(r.neededBy) : '—'}
                      {late ? <small>{t('a trecut')}</small> : null}
                    </td>
                    {buyer ? <td data-label={t('Trimis de')}>{r.createdBy?.name ?? '—'}</td> : null}
                    <td data-label={t('Stadiu')}>
                      <span className={`pill ${st.pill}`}>{st.label}</span>
                      {r.stage === 'rejected' && r.reason ? (
                        <small>{r.reason}</small>
                      ) : r.deliveryDate && r.stage === 'ordered' ? (
                        <small>{t('livrare pe {0}', roDate(r.deliveryDate))}</small>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={buyer ? 6 : 5} className="empty">
                  {all.length
                    ? t('Niciun necesar aici. Schimbă filtrul de sus.')
                    : buyer
                      ? t(
                          'Încă nu a venit niciun necesar. Invită-ți șefii de șantier cu rolul „Șantier” (din Contul firmei → Echipa): nu ocupă loc în pachet.',
                        )
                      : t(
                          'Încă n-ai trimis niciun necesar. Apasă „Necesar nou” și scrie ce trebuie pe șantier.',
                        )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
